import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import CompetitorReviewsState from '@app/model/review/state/CompetitorReviewsState';

const normalizeScore = (score: number) => score > 0 && score <= 1 ? score * 10 : score;

const ReviewsSubCategoryGapAnalysisGraph: React.FunctionComponent<{
	mainHotelReviewsState: CompetitorReviewsState,
	competitorReviewsState: CompetitorReviewsState,
}> = ({ mainHotelReviewsState, competitorReviewsState }) => {

	const options: Highcharts.Options = useMemo(() => {
		const allCompetitorCategoryStates = competitorReviewsState.stateStatisticsForCategories
			.filter(state => state.category.children.length === 0);
		allCompetitorCategoryStates.sort((a, b) => b.statistics.score - a.statistics.score);

		const competitorCategoryStates = allCompetitorCategoryStates.slice(0, 10);

		const gapData = competitorCategoryStates.map(cs => {
			const mainHotelScore = mainHotelReviewsState.stateStatisticsForCategories
				.find(state => state.category.id === cs.category.id && state.category.children.length === 0)?.statistics?.score ?? 0;
			const competitorScore = normalizeScore(cs.statistics.score);
			const normalizedMainHotelScore = normalizeScore(mainHotelScore);
			const gap = competitorScore !== 0 ? ((normalizedMainHotelScore - competitorScore) / competitorScore) * 100 : 0;

			return {
				name: cs.category.name,
				y: gap,
				color: gap >= 0 ? '#10b981' : '#ef4444',
			};
		});

		// Sort by gap value (largest positive to largest negative)
		gapData.sort((a, b) => b.y - a.y);

		const categories = gapData.map(d => d.name);

		return {
			chart: {
				type: 'bar',
				backgroundColor: 'transparent',
			},
			title: {
				text: undefined,
			},
			subtitle: {
				text: 'Relative performance gaps vs competitor (%)',
				style: {
					color: '#9ca3af',
					fontSize: '12px',
				},
			},
			xAxis: {
				categories: categories,
				title: {
					text: undefined,
				},
				labels: {
					style: {
						color: '#9ca3af',
						fontSize: '11px',
					},
				},
			},
			yAxis: {
				min: -100,
				max: 100,
				title: {
					text: undefined,
				},
				labels: {
					format: '{value}%',
					style: {
						color: '#9ca3af',
					},
				},
				plotLines: [{
					value: 0,
					color: '#4b5563',
					width: 2,
					zIndex: 5,
				}],
			},
			tooltip: {
				pointFormat: '<b>{point.y:.1f}%</b>',
				backgroundColor: '#1f2937',
				style: {
					color: '#f9fafb',
				},
			},
			plotOptions: {
				bar: {
					dataLabels: {
						enabled: false,
					},
					borderWidth: 0,
				},
			},
			legend: {
				enabled: false,
			},
			series: [{
				name: 'Gap',
				type: 'bar',
				data: gapData,
			}],
			credits: {
				enabled: false,
			},
		};
	}, [mainHotelReviewsState.stateStatisticsForCategories, competitorReviewsState.stateStatisticsForCategories]);

	return <div className="h-full w-full flex flex-col p-4">
		<div className="w-full flex-1">
			<HighchartsReact
				highcharts={Highcharts}
				highchartsMore={HighchartsMore}
				options={options}
				containerProps={{ style: { height: '90%', width: '100%' } }}
			/>
		</div>
		<div className="flex justify-center gap-6 mt-2">
			<div className="flex items-center gap-2">
				<div className="w-3 h-3 rounded-sm bg-[#10b981]"></div>
				<span className="text-sm text-gray-400">You're ahead</span>
			</div>
			<div className="flex items-center gap-2">
				<div className="w-3 h-3 rounded-sm bg-[#ef4444]"></div>
				<span className="text-sm text-gray-400">Competitor ahead</span>
			</div>
		</div>
	</div>;
};

export default ReviewsSubCategoryGapAnalysisGraph;
